import { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

// Shader program for floor dot grid
const FLOOR_VERT = `
    precision mediump float;
    attribute float alpha;
    varying float vAlpha;
    uniform float uSize;
    uniform float uHeight;
    void main() {
        vAlpha = alpha;
        vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = uSize * projectionMatrix[1][1] * uHeight * 0.5 / -mvPos.z;
        gl_Position = projectionMatrix * mvPos;
    }
`;

const FLOOR_FRAG = `
    precision mediump float;
    uniform sampler2D uMap;
    varying float vAlpha;
    void main() {
        vec4 tex = texture2D(uMap, gl_PointCoord);
        if (tex.a * vAlpha < 0.05) discard;
        gl_FragColor = vec4(tex.rgb, tex.a * vAlpha);
    }
`;

// Controls the falloff rate of dot visibility from center
const FADE_POWER = 1.8;

/** Build a cross-shaped texture for floor dots (#9ACEF8) */
const buildFloorTexture = (): THREE.CanvasTexture => {
    const sz = 64;
    const canvas = document.createElement("canvas");
    canvas.width = sz;
    canvas.height = sz;
    const ctx = canvas.getContext("2d")!;
    const arm = Math.round((sz * 0.18) / 6);
    const c = Math.floor((sz - arm) / 2);
    ctx.fillStyle = "#9ACEF8";
    ctx.fillRect(0, c, sz, arm);
    ctx.fillRect(c, 0, arm, sz);
    return new THREE.CanvasTexture(canvas);
};

export interface FloorDotGridProps {
    floorY?: number;
    extent?: number;
    step?: number;
    dotSize?: number;
}

/**
 * Renders a grid of dots on a floor plane in 3D space.
 * Uses instanced rendering with a shader for performance.
 * Dots fade out radially from the center.
 */
const FloorDotGrid = ({
    floorY = -3.2,
    extent = 20,
    step = 1.2,
    dotSize = 0.5,
}: FloorDotGridProps) => {
    const { size } = useThree();
    const matRef = useRef<THREE.ShaderMaterial>(null);

    // Build texture once at mount
    const texture = useMemo(() => buildFloorTexture(), []);

    // Create geometry with position and alpha attributes
    const geometry = useMemo(() => {
        const positions: number[] = [];
        const alphas: number[] = [];

        for (let x = -extent; x <= extent + 0.001; x += step) {
            for (let z = -extent; z <= extent + 0.001; z += step) {
                positions.push(x, 0, z);
                const distance = Math.sqrt(x * x + z * z);
                const alpha = Math.max(0, 1 - Math.pow(distance / extent, FADE_POWER));
                alphas.push(alpha);
            }
        }

        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
        g.setAttribute("alpha", new THREE.Float32BufferAttribute(alphas, 1));
        return g;
    }, [extent, step]);

    // Create uniform values (update height on each frame)
    const uniforms = useMemo(
        () => ({
            uMap: { value: texture },
            uSize: { value: dotSize },
            uHeight: { value: size.height },
        }),
        [texture, dotSize, size.height]
    );

    // Update shader height uniform when viewport resizes
    useFrame(() => {
        if (matRef.current) {
            matRef.current.uniforms.uHeight.value = size.height;
        }
    });

    return (
        <points geometry={geometry} position={[0, floorY, 0]}>
            <shaderMaterial
                ref={matRef}
                uniforms={uniforms}
                vertexShader={FLOOR_VERT}
                fragmentShader={FLOOR_FRAG}
                transparent
                depthWrite={false}
            />
        </points>
    );
};

export default FloorDotGrid;
